import React, { useEffect, useState } from 'react';
import { Card, List, Space, Typography, Tag, Descriptions, Modal } from 'antd';
import { User, Eye } from 'lucide-react';
import { useCrewStore } from '../stores/useCrewStore';
import { apiService } from '../services/api';
import type { Agent } from '../types';

const { Title, Text } = Typography;

const AgentsPage: React.FC = () => {
  const { agents, setAgents } = useCrewStore();
  const [loading, setLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    setLoading(true);
    try {
      const data = await apiService.getAgents();
      setAgents(data);
    } catch (error) {
      console.error('Failed to load agents:', error);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (agent: Agent) => {
    setSelectedAgent(agent);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <User className="inline-block mr-2" size={24} /> Agents
        </Title>
        <Text type="secondary">View agents currently in use by the system</Text>
      </div>

      {agents.length === 0 && !loading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <User size={64} className="text-gray-300" />
            <Title level={4} style={{ marginTop: 16 }}>No agents in use</Title>
            <Text type="secondary">There are currently no agents being used by the system</Text>
          </div>
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
          dataSource={agents}
          loading={loading}
          renderItem={(agent) => (
            <List.Item>
              <Card
                hoverable
                onClick={() => handleViewDetails(agent)}
                style={{ cursor: 'pointer' }}
              >
                <Card.Meta
                  title={agent.name}
                  description={agent.role}
                />
                <div style={{ marginTop: 16 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary" ellipsis>Goal: {agent.goal}</Text>
                    {agent.llm && (
                      <div>
                        <Tag color="blue">{agent.llm}</Tag>
                      </div>
                    )}
                    <Space size="small">
                      {agent.tools && agent.tools.length > 0 && (
                        <Tag>{agent.tools.length} tool(s)</Tag>
                      )}
                      {agent.allow_delegation && (
                        <Tag color="green">Can Delegate</Tag>
                      )}
                      {agent.verbose && (
                        <Tag color="orange">Verbose</Tag>
                      )}
                    </Space>
                  </Space>
                </div>
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Text type="secondary">
                    <Eye size={14} className="inline-block mr-1" /> Click to view details
                  </Text>
                </div>
              </Card>
            </List.Item>
          )}
        />
      )}

      <Modal
        title="Agent Details"
        open={!!selectedAgent}
        onCancel={() => setSelectedAgent(null)}
        footer={null}
        width={700}
      >
        {selectedAgent && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Name">{selectedAgent.name}</Descriptions.Item>
            <Descriptions.Item label="Role">{selectedAgent.role}</Descriptions.Item>
            <Descriptions.Item label="Goal">{selectedAgent.goal}</Descriptions.Item>
            <Descriptions.Item label="Backstory">{selectedAgent.backstory}</Descriptions.Item>
            {selectedAgent.llm && (
              <Descriptions.Item label="LLM Model">{selectedAgent.llm}</Descriptions.Item>
            )}
            {selectedAgent.tools && selectedAgent.tools.length > 0 && (
              <Descriptions.Item label="Tools">
                <Space size={[0, 8]} wrap>
                  {selectedAgent.tools.map((toolId, idx) => (
                    <Tag key={idx}>{toolId}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            {selectedAgent.max_iter && (
              <Descriptions.Item label="Max Iterations">{selectedAgent.max_iter}</Descriptions.Item>
            )}
            {selectedAgent.max_rpm && (
              <Descriptions.Item label="Max RPM">{selectedAgent.max_rpm}</Descriptions.Item>
            )}
            <Descriptions.Item label="Verbose">
              <Tag color={selectedAgent.verbose ? 'green' : 'default'}>
                {selectedAgent.verbose ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Allow Delegation">
              <Tag color={selectedAgent.allow_delegation ? 'green' : 'default'}>
                {selectedAgent.allow_delegation ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AgentsPage;
